import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { ConfigConstants, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
    updateSystemConditionDeficienciesSelection,
    updateSystemConditionDeficiency,
    updateSystemConditionMeasurementsSelection,
    updateSystemConditionRemarks,
} from '../../store/actions/system-condition.actions';
import {
    getFormAnswers,
    getFormParticipantCopyReadonly,
    getFormSystemCondition,
    getFormSystemConditionCustomerSignature,
    getFormSystemConditionEmployeeSignature,
    getTopLevelFormContent,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Answers } from '../../types/answers';
import { CHOICE_TYPE } from '../../types/choice';
import { ContentBlock } from '../../types/content-block';
import { Signature } from '../../types/signature';
import { DEFICIENCY_SELECTION, MEASUREMENT_SELECTION, SystemCondition } from '../../types/system-condition';

const toggle = (
    control: FormControl,
    readOnly: boolean,
    opts?: {
        onlySelf?: boolean;
        emitEvent?: boolean;
    },
): void => control[readOnly ? 'disable' : 'enable'](opts);

export function forbiddenValueValidator(selection: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const forbidden = control.value === selection;

        return forbidden ? { forbiddenSelection: { value: control.value } } : null;
    };
}

@Component({
    selector: 'pace-forms-system-condition',
    templateUrl: './system-condition.component.html',
    styleUrls: ['./system-condition.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemConditionComponent implements OnInit, OnDestroy {
    @Input()
    public parentForm: FormArray;

    public systemCondition$: Observable<SystemCondition>;
    public systemConditionEmployeeSignature$: Observable<Signature>;
    public systemConditionCustomerSignature$: Observable<Signature>;
    public readOnly$: Observable<boolean>;
    public topLevelContent$: Observable<ContentBlock[]>;
    public answers$: Observable<Answers>;
    public isAstoriaForm$: Observable<boolean>;

    public destroyTrigger$ = new Subject<void>();

    public systemConditionGroup: FormGroup;
    public measurementsRadioGroup: FormGroup;
    public systemConditionRadios: FormGroup;
    public signaturesFormChildren: FormArray;
    public measurementsFormControl: FormControl;
    public deficienciesRadio: FormControl;
    public remarksTextarea: FormControl;
    public employeeSignature: FormControl;
    public customerSignature: FormControl;

    public disableNoDeficiencies$: Observable<boolean>;

    public DEFICIENCY_SELECTION = DEFICIENCY_SELECTION;
    public MEASUREMENT_SELECTION = MEASUREMENT_SELECTION;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
    ) {
        this.systemCondition$ = this.store.select(getFormSystemCondition).pipe(filter(isDefined));
        this.systemConditionCustomerSignature$ = this.store
            .select(getFormSystemConditionCustomerSignature)
            .pipe(filter(isDefined));
        this.systemConditionEmployeeSignature$ = this.store
            .select(getFormSystemConditionEmployeeSignature)
            .pipe(filter(isDefined));
        this.readOnly$ = this.store.select(getFormParticipantCopyReadonly);
        this.topLevelContent$ = this.store.select(getTopLevelFormContent);
        this.answers$ = this.store.select(getFormAnswers).pipe(filter(isDefined));
        this.disableNoDeficiencies$ = this.answers$.pipe(
            withLatestFrom(this.topLevelContent$),
            map(
                ([answers, topLevelContent]) =>
                    topLevelContent
                        .map((contentBlock) => answers[contentBlock.id].answeredCheckpointsNotOK)
                        .reduce((akku, current) => akku + current, 0) > 0,
            ),
        );

        this.isAstoriaForm$ = this.systemCondition$.pipe(map((systemCondition) => !isDefined(systemCondition.choices)));
    }

    public ngOnInit(): void {
        this.signaturesFormChildren = this.formBuilder.array([]);
        this.employeeSignature = this.formBuilder.control({});
        this.customerSignature = this.formBuilder.control({});

        // only available in Astoria forms
        this.measurementsFormControl = this.formBuilder.control(
            {},
            forbiddenValueValidator(MEASUREMENT_SELECTION.NO_SELECTION),
        );
        this.measurementsRadioGroup = this.formBuilder.group({
            measurementsRadio: this.measurementsFormControl,
        });

        this.deficienciesRadio = this.formBuilder.control('');
        this.systemConditionRadios = this.formBuilder.group({
            deficienciesRadio: this.deficienciesRadio,
        });

        this.remarksTextarea = this.formBuilder.control('');

        this.systemConditionGroup = this.formBuilder.group({
            signatures: this.signaturesFormChildren,
            radios: this.systemConditionRadios,
            measurements: this.measurementsRadioGroup,
            employeeSignature: this.employeeSignature,
            customerSignature: this.customerSignature,
            remarksTextarea: this.remarksTextarea,
        });

        Promise.resolve(undefined)
            .then(() => {
                this.parentForm.push(this.systemConditionGroup);
            })
            .catch((error: Error) => {
                this.log.error(
                    `SystemConditionComponent: ngOnInit: Error in parentForm.push: ${JSON.stringify(error)}`,
                );
            });

        combineLatest([this.systemCondition$, this.disableNoDeficiencies$])
            .pipe(withLatestFrom(this.readOnly$), takeUntil(this.destroyTrigger$))
            .subscribe(([[systemCondition, disableNoDeficiencies], readOnly]) => {
                const isCosimaSystemCondition = isDefined(systemCondition.choices);

                this.setDeficiencySelection(systemCondition, disableNoDeficiencies);

                this.toggleControls(isCosimaSystemCondition, readOnly);

                this.setDeficiencyValidator(isCosimaSystemCondition);
            });

        this.systemCondition$.pipe(take(1)).subscribe((systemCondition) => {
            if (!isDefined(systemCondition.choices)) {
                this.setMeasurementsSelection(systemCondition);
                this.setRemarks(systemCondition);
            }
        });

        this.measurementsFormControl.valueChanges
            .pipe(distinctUntilChanged(), withLatestFrom(this.systemCondition$), takeUntil(this.destroyTrigger$))
            .subscribe(([value, systemCondition]: [MEASUREMENT_SELECTION | null, SystemCondition]) => {
                const selected = value ?? MEASUREMENT_SELECTION.NO_SELECTION;

                if (systemCondition.measurementXmlId) {
                    this.store.dispatch(
                        updateSystemConditionMeasurementsSelection({
                            selected,
                            xmlId: systemCondition.measurementXmlId,
                        }),
                    );
                }
            });

        this.deficienciesRadio.valueChanges
            .pipe(
                distinctUntilChanged(),
                withLatestFrom(this.systemCondition$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([value, systemCondition]: [DEFICIENCY_SELECTION | null | string, SystemCondition]) => {
                if (isDefined(systemCondition.choices)) {
                    // cosima updating system deficiency
                    this.store.dispatch(
                        updateSystemConditionDeficiency({
                            xmlId: value === null ? undefined : value,
                        }),
                    );
                } else {
                    const selected = (value as DEFICIENCY_SELECTION) ?? DEFICIENCY_SELECTION.NO_SELECTION;

                    this.store.dispatch(
                        updateSystemConditionDeficienciesSelection({
                            selected,
                            xmlId: systemCondition.deficienciesXmlId,
                        }),
                    );
                }
            });

        this.remarksTextarea.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.systemCondition$),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([value, systemCondition]: [string | null, SystemCondition]) => {
                this.store.dispatch(
                    updateSystemConditionRemarks({ newValue: value ?? '', xmlId: systemCondition.remarksXmlId }),
                );
            });
    }

    public ngOnDestroy(): void {
        // remove form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.systemConditionGroup);
            this.parentForm.removeAt(index);
        }
        this.destroyTrigger$.next();
    }

    private setDeficiencyValidator(isCosimaSystemCondition: boolean): void {
        const deficienciesInvalidValue = isCosimaSystemCondition ? '' : DEFICIENCY_SELECTION.NO_SELECTION;
        this.deficienciesRadio.setValidators([forbiddenValueValidator(deficienciesInvalidValue)]);
    }

    private toggleControls(isCosimaSystemCondition: boolean, readOnly: boolean): void {
        toggle(this.deficienciesRadio, readOnly, { emitEvent: false });

        if (!isCosimaSystemCondition) {
            toggle(this.remarksTextarea, readOnly);
            toggle(this.measurementsFormControl, readOnly, { emitEvent: false });
        }
    }

    private setDeficiencySelection(systemCondition: SystemCondition, disableNoDeficiencies: boolean): void {
        const choices = systemCondition.choices;
        if (isDefined(choices)) {
            const selectedChoice = Object.values(choices)
                .filter(isDefined)
                .find((c) => c.selected);

            if (
                !isDefined(selectedChoice) ||
                (selectedChoice.type === CHOICE_TYPE.CHOICE_POSITIVE && disableNoDeficiencies)
            ) {
                this.deficienciesRadio.reset('');
            } else {
                this.deficienciesRadio.patchValue(selectedChoice.xmlId);
            }
        } else {
            this.deficienciesRadio.patchValue(
                disableNoDeficiencies && systemCondition.deficienciesSelection === DEFICIENCY_SELECTION.NO_DEFICIENCIES
                    ? DEFICIENCY_SELECTION.NO_SELECTION
                    : systemCondition.deficienciesSelection,
            );

            this.deficienciesRadio.updateValueAndValidity();
        }
    }

    private setMeasurementsSelection(systemCondition: SystemCondition): void {
        const measurementSelection = systemCondition.measurementSelection;

        if (isDefined(measurementSelection)) {
            this.measurementsFormControl.patchValue(measurementSelection, { emitEvent: false });
            this.measurementsFormControl.updateValueAndValidity();
        }
    }

    private setRemarks(systemCondition: SystemCondition): void {
        const remarks = systemCondition.remarksValue;

        if (isDefined(systemCondition.remarksValue)) {
            this.remarksTextarea.patchValue(remarks, { emitEvent: false });
        }
    }
}
